/**
 * Subject–Faculty allocation roster — filter by academic year, department, program, semester
 */

import { useMemo } from 'react';
import {
  OPERATIONAL_ACADEMIC_YEAR,
  filterOperationalSemesters,
} from '../config/operationalAcademicContext';
import { programBelongsToDepartment } from './StudentMasterFilters';

export default function AllocationMasterFilters({
  allocationFilters,
  onFiltersChange,
  departments,
  programs,
  academicYears,
  canShowList,
  listShown,
  resultCount,
  onShowList,
  listLoading,
  academicYearLocked = true,
}) {
  const departmentName = allocationFilters.department;
  const activePrograms = (programs || []).filter((p) => p.is_active !== false);
  let programOptions = activePrograms.filter((p) => programBelongsToDepartment(p, departmentName, departments));
  if (departmentName && programOptions.length === 0) {
    programOptions = activePrograms;
  }

  const selectedProgram = programOptions.find((p) => String(p.id) === String(allocationFilters.degreeId));
  const maxSem = Number(selectedProgram?.number_of_semesters || 8);
  const semesterOptions = useMemo(() => {
    const list = Array.from({ length: Math.max(1, Math.min(20, maxSem)) }, (_, i) => i + 1);
    return filterOperationalSemesters(list);
  }, [maxSem]);

  const yearOptions = academicYears?.length ? academicYears : [OPERATIONAL_ACADEMIC_YEAR];

  const handleDepartmentChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      department: value,
      degreeId: '',
      semester: '',
    }));
  };

  const handleProgramChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      degreeId: value,
      semester: '',
    }));
  };

  const statusMessage = (() => {
    if (!allocationFilters.academicYear) return 'Select academic year.';
    if (!allocationFilters.department) return 'Select a department.';
    if (!allocationFilters.degreeId) return 'Select a program, then click Show.';
    if (!listShown) {
      const semHint = allocationFilters.semester !== '' && allocationFilters.semester != null
        ? `semester ${allocationFilters.semester}`
        : 'all semesters';
      return `Ready — click Show to load allocations (${semHint}).`;
    }
    return `Showing ${resultCount ?? 0} allocation${resultCount === 1 ? '' : 's'}.`;
  })();

  return (
    <div className="student-roster-panel subject-roster-panel allocation-roster-panel">
      <div className="student-roster-panel-head">
        <div>
          <h3 className="student-roster-title">Allocation filters</h3>
          <p className="student-roster-subtitle">Department, program, and optional semester</p>
        </div>
      </div>

      <div className="student-roster-filters faculty-roster-filters">
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="alloc-filter-year">Academic year</label>
          <select
            id="alloc-filter-year"
            value={allocationFilters.academicYear || OPERATIONAL_ACADEMIC_YEAR}
            onChange={(e) => onFiltersChange((f) => ({ ...f, academicYear: e.target.value }))}
            className="input student-roster-select"
            disabled={academicYearLocked}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="alloc-filter-dept">Department</label>
          <select
            id="alloc-filter-dept"
            value={allocationFilters.department}
            onChange={(e) => handleDepartmentChange(e.target.value)}
            className="input student-roster-select"
          >
            <option value="">Select department</option>
            {(departments || []).map((d) => (
              <option key={d.id || d.code || d.name} value={d.name}>
                {d.code ? `${d.code} — ${d.name}` : d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="alloc-filter-program">Program</label>
          <select
            id="alloc-filter-program"
            value={allocationFilters.degreeId}
            onChange={(e) => handleProgramChange(e.target.value)}
            className="input student-roster-select"
            disabled={!departmentName}
          >
            <option value="">Select program</option>
            {programOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.program_code || p.code} — {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="alloc-filter-sem">Semester</label>
          <select
            id="alloc-filter-sem"
            value={allocationFilters.semester === '' || allocationFilters.semester == null ? '' : allocationFilters.semester}
            onChange={(e) => {
              const v = e.target.value;
              onFiltersChange((f) => ({
                ...f,
                semester: v === '' ? '' : Number(v),
              }));
            }}
            className="input student-roster-select"
            disabled={!allocationFilters.degreeId}
          >
            <option value="">Any</option>
            {semesterOptions.map((s) => (
              <option key={s} value={s}>Semester {s}</option>
            ))}
          </select>
        </div>

        <div className="student-roster-field student-roster-field-action">
          <span className="student-roster-label student-roster-label--invisible">Show</span>
          <button
            type="button"
            className="btn-primary student-roster-show-btn"
            disabled={!canShowList || listLoading}
            onClick={onShowList}
          >
            {listLoading ? 'Loading…' : 'Show'}
          </button>
        </div>
      </div>

      <p className="student-roster-status">{statusMessage}</p>
    </div>
  );
}
